export type LatLon = { lat: number; lon: number };

const R = 6371008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in metres (haversine). */
export function distanceM(a: LatLon, b: LatLon): number {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial great-circle bearing from `from` to `to`, in [0,360). */
export function bearingDeg(from: LatLon, to: LatLon): number {
  const φ1 = rad(from.lat);
  const φ2 = rad(to.lat);
  const Δλ = rad(to.lon - from.lon);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];

/** 16-point compass label in Italian (O = ovest). */
export function cardinalIt(d: number): string {
  const n = ((d % 360) + 360) % 360;
  return POINTS[Math.round(n / 22.5) % 16];
}

/** Signed smallest rotation from `current` to `target`, in (-180, 180]. */
export function angleDelta(target: number, current: number): number {
  const d = (((target - current) % 360) + 360) % 360;
  return d > 180 ? d - 360 : d;
}

/** n equally spaced points from a to b (linear in lat/lon; fine for < 50 km). */
export function interpolate(a: LatLon, b: LatLon, n: number): LatLon[] {
  if (n < 2) return [a];
  const out: LatLon[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    out.push(i === 0 ? a : i === n - 1 ? b : { lat: a.lat + (b.lat - a.lat) * t, lon: a.lon + (b.lon - a.lon) * t });
  }
  return out;
}
